import React, { useState } from 'react';
import { X, Mail, ArrowRight } from 'lucide-react';
import Button from './Button';
import { useAuth } from '../context/AuthContext';
import './AuthModal.css';

export default function AuthModal({ isOpen, onClose }) {
  const [tab, setTab] = useState('login');
  const { signIn, signUp, signInWithGoogle } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [nationality, setNationality] = useState('');
  const [userType, setUserType] = useState('student');
  const [interests, setInterests] = useState([]);

  const toggleInterest = (interest) => {
    setInterests(prev => 
      prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]
    );
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let res;
      if (tab === 'login') {
        res = await signIn(email, password);
      } else {
        res = await signUp(email, password, {
          display_name: displayName,
          first_name: firstName,
          last_name: lastName,
          phone_number: phoneNumber,
          nationality,
          user_type: userType,
          interests
        });
      }
      
      if (res.error) throw res.error;
      onClose(); // Success!
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ overflowY: 'auto', padding: '40px 0' }}>
      <div className="modal-container animate-fade-in" style={{ margin: 'auto', maxHeight: 'none', maxWidth: tab === 'signup' ? '500px' : '400px' }}>
        <button className="modal-close" onClick={onClose} aria-label="Close modal">
          <X size={20} />
        </button>
        
        <div className="modal-header">
          <h2 className="text-h2">{tab === 'login' ? 'Welcome back' : 'Join DesignHub'}</h2>
          <p className="text-secondary text-caption">
            {tab === 'login' ? 'Log in to download and upload templates.' : 'Create an account to start sharing your work.'}
          </p>
        </div>
        
        <div className="modal-tabs">
          <button 
            className={`modal-tab ${tab === 'login' ? 'active' : ''}`}
            onClick={() => { setTab('login'); setError(''); }}
          >
            Log In
          </button>
          <button 
            className={`modal-tab ${tab === 'signup' ? 'active' : ''}`}
            onClick={() => { setTab('signup'); setError(''); }}
          >
            Sign Up
          </button>
        </div>
        
        <div className="modal-content">
          {tab === 'login' && (
            <div className="social-auth">
              <Button variant="secondary" className="w-full" onClick={() => signInWithGoogle()}>
                <Mail size={18} /> Continue with Google
              </Button>
            </div>
          )}
          
          {tab === 'login' && (
            <div className="auth-divider">
              <span className="text-caption text-tertiary">OR</span>
            </div>
          )}
          
          <form className="email-auth-form" onSubmit={handleSubmit}>
            {error && <div style={{ color: 'var(--status-error)', fontSize: '14px', marginBottom: '12px' }}>{error}</div>}
            
            {tab === 'signup' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label>First Name</label>
                    <input type="text" className="form-input" value={firstName} onChange={e => setFirstName(e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input type="text" className="form-input" value={lastName} onChange={e => setLastName(e.target.value)} required />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="displayName">Display Name</label>
                  <input type="text" id="displayName" placeholder="Design Guru" className="form-input" value={displayName} onChange={e => setDisplayName(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Phone Number (Private)</label>
                  <input type="tel" className="form-input" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Nationality</label>
                  <select className="form-input" value={nationality} onChange={e => setNationality(e.target.value)} required>
                    <option value="">Select Nationality...</option>
                    <option value="Ghanaian">Ghanaian</option>
                    <option value="Nigerian">Nigerian</option>
                    <option value="Kenyan">Kenyan</option>
                    <option value="South African">South African</option>
                    <option value="American">American</option>
                    <option value="British">British</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>I am a...</label>
                  <select className="form-input" value={userType} onChange={e => setUserType(e.target.value)} required>
                    <option value="student">Student</option>
                    <option value="freelancer">Freelancer</option>
                    <option value="business_owner">Business Owner</option>
                    <option value="hobbyist">Hobbyist</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>What are you looking for? (Interests)</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                    {['UI Designs', 'Flyers', 'Mockups', 'Logos', 'Social Media'].map(interest => (
                      <label key={interest} style={{ 
                        display: 'flex', alignItems: 'center', gap: '4px', 
                        padding: '6px 12px', border: '1px solid var(--border-subtle)', borderRadius: '20px',
                        cursor: 'pointer', fontSize: '14px',
                        backgroundColor: interests.includes(interest) ? 'var(--brand-primary)' : 'transparent',
                        color: interests.includes(interest) ? '#fff' : 'inherit'
                      }}>
                        <input type="checkbox" style={{ display: 'none' }} checked={interests.includes(interest)} onChange={() => toggleInterest(interest)} />
                        {interest}
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
            
            <div className="form-group" style={{ marginTop: '12px' }}>
              <label htmlFor="email">Email</label>
              <input type="email" id="email" placeholder="you@example.com" className="form-input" value={email} onChange={e => setEmail(e.target.value)} required />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input type="password" id="password" placeholder="••••••••" className="form-input" value={password} onChange={e => setPassword(e.target.value)} required />
            </div>
            
            <Button variant="primary" className="w-full mt-4" disabled={loading}>
              {loading ? 'Processing...' : tab === 'login' ? 'Log In' : 'Create Account'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
